import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { getTasks, updateTask } from '@/lib/api';
import { ListTodo, Check, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await getTasks(params);
      setTasks(data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      toast.success('Task updated');
      load();
    } catch { toast.error('Failed to update task'); }
  };

  const priorityColor = { high: 'destructive', medium: 'secondary', low: 'outline' };
  const statusIcon = {
    pending: <Clock className="h-4 w-4 text-muted-foreground" />,
    in_progress: <Loader2 className="h-4 w-4 text-primary animate-spin" />,
    completed: <Check className="h-4 w-4 text-[hsl(142,56%,36%)]" />,
    cancelled: <AlertCircle className="h-4 w-4 text-muted-foreground" />
  };

  return (
    <div data-testid="tasks-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Tasks</h1>
          <p className="text-sm text-muted-foreground">Manage compliance tasks and follow-ups</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : tasks.length === 0 ? (
        <Card className="rounded-xl">
          <CardContent className="py-16 text-center">
            <ListTodo className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">No tasks found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tasks.map(task => (
            <Card key={task.id} className="rounded-xl border shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-pop)] transition-shadow duration-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="mt-1">{statusIcon[task.status]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{task.title}</span>
                      <Badge variant={priorityColor[task.priority]} className="text-xs">{task.priority}</Badge>
                      <Badge variant="outline" className="text-xs">{task.task_type?.replace('_', ' ')}</Badge>
                    </div>
                    {task.vendor_name && <p className="text-xs text-muted-foreground">Vendor: {task.vendor_name}</p>}
                    {task.due_date && <p className="text-xs text-muted-foreground">Due: {new Date(task.due_date).toLocaleDateString()}</p>}
                  </div>
                  <div className="flex gap-1">
                    {task.status !== 'completed' && (
                      <Button variant="outline" size="sm" onClick={() => handleStatusChange(task.id, 'completed')}>
                        <Check className="h-3 w-3 mr-1" /> Complete
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
